import React, { useState } from "react";
import DataGridTable from "../DataGridTable/DataGridTable";
import { getFormattedDate, getFormattedTime } from "../../utility/formateDateAndTime";
import { Tooltip, Button, Dialog, DialogTitle, DialogContent, DialogActions, Grid } from "@mui/material";
import { IFeed } from "../../interface/IFeed";
import EditFeed from "./EditFeed";
import ConfirmationModal from "../common/ConfirmationModal";
import UpdateStatusIcon from "../../assets/icons/activate-blog-icon.svg";
import DeleteIcon from "../../assets/icons/delete-icon.svg";
import { serviceProvider } from "../../provider/serviceProvider";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import BMJTypography from "../UI/TypographyComponent/BMJTypography";
import CommonModal from "../modal/CommonModal";

const FeedTable = ({ pageState, setPageState, getAllFeeds }: any) => {
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedFeed, setSelectedFeed] = useState<any>(null);
  const [actionType, setActionType] = useState<"activate" | "deactivate" | "delete" | null>(null);
  const [openFutureTimeDialog, setOpenFutureTimeDialog] = useState(false);
  const handleConfirmAction = (feed: any, type: "activate" | "deactivate" | "delete") => {
    setSelectedFeed(feed);
    setActionType(type);
    setOpenConfirm(true);
  };

const handleAction = async () => {
  if (!selectedFeed || !actionType) return;

  try {
    if (actionType === "activate") {
      // ⏰ Prevent activation if the deactivation time is in the past
      if (
        selectedFeed?.datetimeToDeactivate &&
        dayjs(selectedFeed.datetimeToDeactivate).isBefore(dayjs())
      ) {
        setOpenFutureTimeDialog(true);
        setOpenConfirm(false);
        return;
      }

      const { id, ...rest } = selectedFeed;
      const payload = {
        ...rest,
        active: true,
      };

      const response = await serviceProvider.feedService.put(selectedFeed.feedId, payload);

      if (response?.responseCode === 200) {
        toast.success(response.message || "Feed activated successfully");
        setOpenConfirm(false);
        const query = `search?isActive=false`;
        getAllFeeds(query);
      }
    } else if (actionType === "deactivate") {
      const { id, ...rest } = selectedFeed;
      const payload = {
        ...rest,
        active: false,
      };

      const response = await serviceProvider.feedService.put(selectedFeed.feedId, payload);

      if (response?.responseCode === 200) {
        toast.success(response.message || "Feed deactivated successfully");
        setOpenConfirm(false);
        const query = `search?isActive=true`;
        getAllFeeds(query);
      }
    } else if (actionType === "delete") {
      const response = await serviceProvider.feedService.delete(selectedFeed.feedId);
      if (response?.responseCode === 204) {
        toast.success(response?.message || "Feed deleted successfully");
        setOpenConfirm(false);
        const query = `search?isActive=${selectedFeed.active}`;
        getAllFeeds(query);
      }
    }
  } catch (err: any) {
    toast.error(err?.data?.error?.message || "Something went wrong");
  }
};


  const columns = [
    {
      field: "feedTitle",
      headerName: "Title",
      width: 400,
      renderCell: (params: any) => (
        <Tooltip title={params.row?.feedTitle}>
          <span>{params.row?.feedTitle}</span>
        </Tooltip>
      ),
    },
    {
      field: "datetimeToDeactivate",
      headerName: "DateTime to deactivate",
      width: 300,
      renderCell: (params: any) => (
        <span>{`${getFormattedDate(params?.row?.datetimeToDeactivate)} ${getFormattedTime(
          params?.row?.datetimeToDeactivate
        )}`}</span>
      ),
    },

    {
      field: "cta_link",
      headerName: "Redirect Link",
      width: 300,
    },
    {
      field: "cta_name",
      headerName: "CTA Label",
      width: 200,
    },
    {
      field: "manage",
      headerName: "Manage",
      width: 300,
      renderCell: (params: any) => (
        <Grid container columnSpacing={2}>
          <Grid item >
            <EditFeed feedDetails={params.row} getAllFeeds={getAllFeeds} />
            </Grid>
          <Grid item >
            
            <Tooltip
              title={`${params.row.active ? "Deactivate" : "Activate"} feed`}
            >
              <Button
                onClick={() => handleConfirmAction(params.row, params.row.active ? "deactivate" : "activate")}

              >
                <img
                  src={UpdateStatusIcon}
                  alt="icon"
                  height="20px"
                  width="20px"
                />
              </Button>
            </Tooltip>
          </Grid>
          <Grid item >
            <Tooltip
              title={"Delete feed"}
            >
              <Button
                onClick={() => handleConfirmAction(params.row, "delete")}

              >
                <img
                  src={DeleteIcon}
                  alt="icon"
                  height="20px"
                  width="20px"
                />
              </Button>
            </Tooltip>

          </Grid>
        </Grid>
      ),
    },
  ];

  const rows = pageState?.data?.map((feed: IFeed) => {
    let cta_name = "";
    let cta_link = "";

    try {
      if (feed.redirect_link) {
        const parsed =
          typeof feed.redirect_link === "string"
            ? JSON.parse(feed.redirect_link)
            : feed.redirect_link;

        cta_name = parsed?.cta_name || "";
        cta_link = parsed?.cta_link || "";
      }
    } catch {
      cta_link = feed.redirect_link || "";
    }

    return {
      ...feed,
      id: feed?.feedId,
      cta_name,
      cta_link,
    };
  });


  return (
    <>
      <DataGridTable pageState={pageState} setPageState={setPageState} rows={rows} columns={columns} />

      {/* 🔹 Confirmation Dialog */}
      <ConfirmationModal open={openConfirm} onClose={() => setOpenConfirm(false)} onConfirm={handleAction}>
        {/* <DialogTitle>Confirm Action</DialogTitle> */}
        <DialogContent>
          Are you sure you want to{" "}
          <strong>
            {actionType === "delete"
              ? "delete"
              : actionType === "activate"
                ? "activate"
                : "deactivate"}
          </strong>{" "}
          this feed?
        </DialogContent>
        <DialogActions>

        </DialogActions>
      </ConfirmationModal>
      <CommonModal
        open={openFutureTimeDialog}
        handleCloseModal={() => setOpenFutureTimeDialog(false)}
        className="feedFutureModal"
        modalBody={
          <Grid container
            justifyContent={"center"}
            textAlign={"center"}
          >
            <BMJTypography variant="body1">
              Please select a <b>future date and time</b> for deactivation
              before activating this feed.
            </BMJTypography>

            <Button
              onClick={() => setOpenFutureTimeDialog(false)}
              className="rounded-btn-fill"
              sx={{ mt: 2 }}
            >
              OK
            </Button>
          </Grid>
        }
      >

      </CommonModal>

    </>
  );
};

export default FeedTable;
